import {Box, IconButton, List, ListItem, ListItemAvatar, ListItemText, Tooltip, Typography} from '@mui/material';
import {Check, Star, Timer} from '@mui/icons-material';
import UserAvatar from '../UserAvatar';

const QuizPlayerList = ({ 
  players, 
  currentUser, 
  currentQuestionIdx,
  animatingScores 
}) => {
  return (
    <List>
      {players.map((player, index) => {
        const hasAnswered = player.answers?.some(
          a => a.questionIndex === currentQuestionIdx
        );
        const displayScore = animatingScores[player.id] ?? (player.score ?? 0);
        const lastAnswer = player.answers?.find(a => a.questionIndex === currentQuestionIdx);
        const isAnimating = player.id in animatingScores;
        const showBonus = isAnimating && lastAnswer?.isCorrect;
        const bonusPoints = lastAnswer?.timeBonus || 0;
        
        return (
          <ListItem
            key={player.id}
            sx={{
              bgcolor: player.id === currentUser.uid ? 'rgba(28, 39, 60, 0.8)' : 'rgba(28, 39, 60, 0.6)',
              backdropFilter: 'blur(10px)',
              borderRadius: 1,
              mb: 1,
              transition: 'all 0.3s ease',
              position: 'relative',
              overflow: 'hidden',
              animation: `slideIn 0.3s ease-out ${index * 0.1}s both`,
              transform: 'translateX(0)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              '&:hover': {
                bgcolor: 'rgba(28, 39, 60, 0.9)',
                transform: 'translateX(8px)'
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)',
                opacity: 0,
                transition: 'opacity 0.3s ease'
              },
              '&:hover::before': {
                opacity: 1
              }
            }}
          >
            <ListItemAvatar>
              <Box sx={{ position: 'relative' }}>
                <UserAvatar
                  src={player.hasRpmAvatar ? player.rpmModelUrl?.replace('.glb', '.png') : player.photoURL}
                  alt={player.displayName}
                  size={40}
                  hasRpmAvatar={Boolean(player.hasRpmAvatar)}
                  rpmModelUrl={player.rpmModelUrl}
                  rpmAvatarConfig={player.rpmAvatarConfig}
                  rpmAvatarType={player.rpmAvatarType}
                  sx={{
                    border: '2px solid',
                    borderColor: index === 0 ? '#ffd700' : 
                               index === 1 ? '#c0c0c0' : 
                               index === 2 ? '#cd7f32' : 'rgba(255, 255, 255, 0.1)',
                    boxShadow: index <= 2 ? '0 0 10px rgba(255, 215, 0, 0.3)' : 'none'
                  }}
                />
              </Box>
            </ListItemAvatar>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography 
                    variant="subtitle2"
                    sx={{
                      fontWeight: 'bold',
                      color: player.id === currentUser.uid ? 'var(--color-secondary)' : 'var(--color-text-primary)',
                      textShadow: index <= 2 ? '0 0 8px rgba(255, 215, 0, 0.3)' : '0 1px 2px rgba(0,0,0,0.3)'
                    }}
                  >
                    {index + 1}. {player.displayName}
                  </Typography>
                  {player.isGameMaster && (
                    <Tooltip 
                      title="Game Master"
                      placement="top"
                    >
                      <IconButton 
                        size="small" 
                        disableRipple 
                        className="icon-button-transparent"
                      >
                        <Star className="game-master-icon" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              }
              secondary={
                <Typography component="div" variant="body2">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      component="span"
                      sx={{
                        color: isAnimating ? 'var(--color-secondary)' : 'var(--color-text-secondary)',
                        transition: 'all 0.3s ease',
                        fontWeight: isAnimating ? 'bold' : 'normal',
                        textShadow: isAnimating ? '0 0 8px rgba(255, 160, 0, 0.3)' : '0 1px 2px rgba(0,0,0,0.3)'
                      }}
                    >
                      {displayScore} Punkte
                    </Typography>
                    {showBonus && bonusPoints > 0 && (
                      <Typography
                        component="span"
                        sx={{
                          color: '#4caf50',
                          fontWeight: 'bold',
                          display: 'inline-block',
                          marginLeft: '8px',
                          animation: 'slideInFadeOut 2s ease-out forwards',
                          textShadow: '0 0 8px rgba(76, 175, 80, 0.3)'
                        }}
                      >
                        +{bonusPoints}
                      </Typography>
                    )}
                  </Box>
                </Typography>
              }
            />
            {hasAnswered ? (
              <Box 
                sx={{ 
                  width: 24, 
                  height: 24, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center'
                }}
              >
                <Check 
                  sx={{ 
                    fontSize: 20, 
                    color: '#4caf50'
                  }} 
                />
              </Box>
            ) : (
              <Box 
                sx={{ 
                  width: 24, 
                  height: 24, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  animation: 'pulse 2s infinite'
                }}
              >
                <Timer 
                  sx={{ 
                    fontSize: 20, 
                    color: 'var(--color-text-secondary)',
                    opacity: 0.7
                  }} 
                />
              </Box>
            )}
          </ListItem>
        );
      })}
    </List>
  );
};

export default QuizPlayerList; 